import { WidgetBase } from '@dojo/framework/core/WidgetBase';
import { Store } from '@dojo/framework/stores/Store';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';

import { DateTime } from 'luxon';

import isNumber from 'lodash-es/isNumber';
import { logQuiz } from 'logger';
import { userstore, mpeChange, mpeRemove } from 'lcc-state';

import { LinkedList } from 'collections';
import { QuizMeta, QuizRPC, InfoRPC, ProgressionSvc, QuestionType, IResult, StartQuiz, Question } from '../interfaces';
import {
	setCursor,
	setQuestion,
	incrCursor,
	decrCursor,
	setInreview,
	setHasQueue,
	setActiveQuizSection,
	setProgressionIsrunning
} from './state';
import { MCSelectorProperties } from '../questions/mc-selector';
import timesync from 'timesync';
import { QueueLoader, allmpequeues } from './queueloader';

import css from '../css/questionfeedback.m.css';
import { QuizSectionsMessage } from '../quizsection';

export interface QuestionListMPEProperties {
	quizstore: Store<QuizMeta>;
	svc: ProgressionSvc;
	finalize: () => void;
	midreview: () => void;
	startquiz: StartQuiz;
	quiz: QuizRPC;
	info: InfoRPC;
	question: any;
	isaudit: boolean;
	resultid?: string;
	instancekey?: string;
	quizsections?: QuizSectionsMessage;
}

const ISCOMPLETE = 1;
const dummyList = new LinkedList<Question>();

export class QuestionListMPE extends WidgetBase<QuestionListMPEProperties> {
	private cursors: number[] = [];
	private loc: number = 0;
	private inreview: boolean = false;
	private instanceId: string | undefined;
	private originalId: string | undefined;
	private detaching: boolean = false;
	private hasReviewed: boolean = false;
	private _propsInited: boolean = false;
	private quizcursor: number = 0;
	private sectioncount: number = 0;
	private sectionstart: number = 0;
	private _inFinishSection = false;
	private _inFinishQuiz = false;

	public get loader(): QueueLoader | undefined {
		const { instanceId, detaching } = this;
		const { svc, quizstore, resultid } = this.properties;
		const { quizcursor } = this;
		if (instanceId !== undefined) {
			if (allmpequeues.has(instanceId)) {
				const loaders = allmpequeues.get(instanceId);
				if (loaders && loaders.length > quizcursor) {
					return loaders[quizcursor];
				}
			}
			const quizsections = this.properties.quizsections;
			const hasSection = Boolean(quizsections && quizcursor < quizsections.sections.length);
			if (!detaching && hasSection) {
				const section = quizsections!.sections[quizcursor];
				this.sectionstart = section.questionrange.start;
				const qloader = new QueueLoader({
					svc,
					quizstore,
					firstquestion: section.questionrange.start,
					numberofquestions: section.questionrange.count,
					instanceId,
					useQueue: false,
					resultid
				});
				const loaders = allmpequeues.get(instanceId) || [];
				loaders.push(qloader);
				allmpequeues.set(instanceId, loaders);
				return qloader;
			}
		}
		return undefined;
	}

	private setActiveSection(params: { cursor: number }): void {
		const { quizstore, quizsections, quiz } = this.properties;
		const { cursor } = params;
		this.quizcursor = cursor;
		let { sectioncount } = this;
		if (sectioncount === 0) {
			sectioncount = this.sectioncount = quizsections !== undefined ? quizsections.sections.length : 0;
		}
		const quizid = quiz.id;

		if (quizsections !== undefined && cursor < sectioncount) {
			const section = quizsections.sections[cursor];
			const timelimit = section.timelimit;
			const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
			const getFinish = () => {
				if (quiz.use_restart) {
					const quizmpe = userstore.get(userstore.path('mpe', quiz.id));
					if (quizmpe) {
						return DateTime.fromISO(quizmpe.timeout); // actually finish time
					}
				}
				return now.plus({ minutes: timelimit });
			};
			const sectionfinish = getFinish();
			const timeout = sectionfinish.diff(now, ['hours', 'minutes', 'seconds']).toObject();
			logQuiz.debug(() => `timeout for section ${cursor}: ${timeout} = ${sectionfinish}`);
			setActiveQuizSection(quizstore)({ cursor, timeout, section });
			mpeChange({ quizid, item: { sectioncursor: 0, timeout: sectionfinish.toISO(), inreview: false } });
		} else {
			logQuiz.debug(() => `clearing active quiz section (quizsections is undefined or cursor >= sectioncount)`);
			setActiveQuizSection(quizstore)({ cursor: 0, timeout: {}, section: undefined });
			mpeChange({ quizid, item: { sectioncursor: 0, timeout: '', inreview: false } });
		}
	}

	public firstLoader(): QueueLoader | undefined {
		this.setActiveSection({ cursor: 0 });
		return this.loader;
	}

	public nextLoader(): QueueLoader | undefined {
		this.setActiveSection({ cursor: this.quizcursor + 1 });
		return this.loader;
	}

	public get queue(): LinkedList<Question> | undefined {
		const { loader } = this;
		return loader && (loader.queue as LinkedList<Question>);
	}

	public stopLoading(): void {
		logQuiz.debug(() => `QuestionListMPE#stopLoading for ${this.instanceId}`);
		const qloader = this.loader;
		if (qloader === undefined || qloader.stoppedloading) {
			return;
		}
		qloader.stopLoading();
	}

	protected onDetach(): void {
		logQuiz.debug(() => `QuestionListMPE#onDetach for ${this.originalId}`);
		this.detaching = true;
		const qloader = this.loader;
		const { instanceId } = this;
		qloader && qloader.stopLoading();
		instanceId && allmpequeues.delete(instanceId);
	}

	protected has_next_section(): boolean {
		const quizsections = this.properties.quizsections;
		return Boolean(quizsections && this.quizcursor + 1 < this.sectioncount);
	}

	protected get_question_array(): Question[] {
		const { queue } = this;
		if (queue) {
			return queue.toArray();
		}
		return [];
	}

	protected has_next_question(): boolean {
		if (this.cursors.length > 0) {
			return this.loc + 1 < this.cursors.length;
		}
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const { queue } = this;
		const cursor = get(path('cursor'));
		if (queue && cursor >= queue.length) {
			return Boolean(cursor - this.sectionstart + 1 < queue.length);
		}
		return Boolean(queue && cursor + 1 < queue.length);
	}

	private setQuestion() {
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const cursor = get(path('cursor'));
		const queue = this.queue!;
		const question =
			cursor >= queue.length ? queue.elementAtIndex(cursor - this.sectionstart)! : queue.elementAtIndex(cursor)!;
		if (!question.starttime) {
			question.starttime = timesync.now().toJSON();
		}
		setQuestion(quizstore)({ question });
	}

	protected next_question(): void {
		const { quizstore } = this.properties;
		if (this.has_next_question()) {
			if (this.cursors.length > 0) {
				this.loc += 1;
				const cursor = this.cursors[this.loc];
				setCursor(quizstore)({ cursor });
				this.setQuestion();
			} else {
				incrCursor(quizstore);
				this.setQuestion();
			}
		}
	}

	protected has_previous_question(): boolean {
		if (this.cursors.length > 0) {
			return this.loc > 0;
		}
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const cursor = get(path('cursor'));
		if (this.queue && cursor >= this.queue.length) {
			return Boolean(cursor - this.sectionstart > 0);
		}
		return cursor > 0;
	}

	protected previous_question(): void {
		const { quizstore } = this.properties;
		if (this.has_previous_question()) {
			if (this.cursors.length > 0) {
				this.loc -= 1;
				const cursor = this.cursors[this.loc];
				setCursor(quizstore)({ cursor });
				this.setQuestion();
			} else {
				decrCursor(quizstore);
				this.setQuestion();
			}
		}
	}

	protected show_review(flag: boolean): void {
		const { quizstore } = this.properties;
		this.inreview = flag;
		if (flag) {
			this.hasReviewed = true;
			this._inFinishSection = false;
		}
		setInreview(quizstore)({ inreview: flag });
		logQuiz.info(() => `${flag ? 'show' : 'hide'} review`);
		this.invalidate();
	}

	protected finish_quiz(): void {
		if (this._inFinishQuiz) {
			return;
		}
		this._inFinishQuiz = true;
		const { finalize, svc, isaudit, quizstore, quiz } = this.properties;
		setQuestion(quizstore)({ question: undefined });
		mpeRemove({ quizid: quiz.id });
		if (isaudit) {
			finalize();
		} else {
			svc.finish_quiz().then(() => {
				finalize();
			});
		}
	}

	protected finish_section(): void {
		if (this._inFinishSection) {
			return;
		}
		this._inFinishSection = true;
		const { quizstore, quiz } = this.properties;
		setQuestion(quizstore)({ question: undefined });
		setProgressionIsrunning(quizstore)({ isrunning: 'quiz' });
		mpeRemove({ quizid: quiz.id });
		const loader = this.nextLoader()!;
		setCursor(quizstore)({ cursor: 0 });
		const { sectionstart } = this;
		loader.load_questions({ cursor: sectionstart, status: 'question' }, false);
		// set up for turning off prior section review
		this.cursors = [];
		this.loc = 0;
		setCursor(quizstore)({ cursor: 0 });
		this.show_review(false);
	}

	protected update_results(results: IResult[]): Promise<any> {
		const {
			info: { qrid },
			quiz: { id },
			svc,
			isaudit
		} = this.properties;

		if (isaudit) {
			return Promise.resolve(true);
		}

		results.forEach((result) => {
			result.qrlocator.qrid = qrid;
			result.qrlocator.quiz = id;
		});
		return svc.update_results({ results });
	}

	protected filter_on(name: 'marked' | 'complete' | 'incomplete' | 'all' | number): void {
		const { quizstore } = this.properties;
		if (isNumber(name)) {
			this.cursors = [];
			setCursor(quizstore)({ cursor: name });
			this.setQuestion();
			return;
		}
		if (name === 'all') {
			this.cursors = [];
			setCursor(quizstore)({ cursor: 0 });
			this.setQuestion();
			return;
		}
		const cursors: number[] = [];
		this.queue!.forEach((q) => {
			switch (name) {
				case 'marked':
					if (q.marked) {
						cursors.push(q.cursor);
					}
					break;
				case 'complete':
					if (q.flags[ISCOMPLETE]) {
						cursors.push(q.cursor);
					}
					break;
				case 'incomplete':
					if (!q.flags[ISCOMPLETE]) {
						cursors.push(q.cursor);
					}
					break;
			}
		});
		if (cursors.length > 0) {
			setCursor(quizstore)({ cursor: cursors[0] });
			this.loc = 0;
			this.cursors = cursors;
			this.setQuestion();
		}
	}

	protected check_for(name: 'marked' | 'complete' | 'incomplete' | 'all'): boolean {
		if (name === 'all') {
			return true;
		}
		const { queue = dummyList } = this;
		let flag = false;
		queue.forEach((q) => {
			switch (name) {
				case 'marked':
					flag = flag || !!q.marked;
					break;
				case 'complete':
					flag = flag || q.flags[ISCOMPLETE];
					break;
				case 'incomplete':
					flag = flag || !q.flags[ISCOMPLETE];
					break;
			}
		});
		return flag;
	}

	protected onAttach(): void {
		const { quizstore, quizsections } = this.properties;
		if (allmpequeues.size > 0) {
			logQuiz.debug(() => `already have QuestionListMPE ${Array.from(allmpequeues.keys()).join(', ')}`);
			this.originalId = this.instanceId;
			this.instanceId = '--bad--';
			this.detaching = true;
			return;
		}

		setHasQueue(quizstore)({ hasqueue: true });

		const { startquiz, instancekey, isaudit, quiz } = this.properties;
		if (this.instanceId && this.instanceId !== instancekey) {
			logQuiz.debug(() => `QuestionListMPE#onAttach for defunct ${this.originalId}`);
			return;
		}
		this.instanceId = instancekey;
		this.originalId = instancekey;
		logQuiz.debug(() => `QuestionListMPE#onAttach for ${this.instanceId}`);

		const { get, path } = userstore;
		const m = get(path(`mpe`));
		const mpe = m ? m[quiz.id] : { sectioncursor: 0, timeout: '' };
		if (mpe) {
			logQuiz.debug(() => `mpe state = {sectioncursor: ${mpe.sectioncursor}, timeout: '${mpe.timeout}'}`);
		}

		const sections = quizsections!.sections;
		this.sectioncount = sections.length;

		let loader = this.firstLoader();
		let idx = 0;
		while (loader !== undefined) {
			loader.load_questions(startquiz, isaudit);
			const range = sections[idx].questionrange;
			const chunkloc = range.start + range.count - 1;
			idx += 1;
			if (startquiz.cursor > chunkloc) {
				loader = this.nextLoader();
			} else {
				loader = undefined;
			}
		}
	}

	protected render(): DNode {
		const { instancekey, quizstore } = this.properties;
		const { detaching, instanceId } = this;
		if (detaching || (instanceId && instanceId !== instancekey)) {
			logQuiz.debug(() => `QuestionListMPE#render defunct instance ${this.originalId}`);
			return null;
		}

		const { isaudit } = this.properties;
		const { question } = this.properties;
		if (!question) {
			return v('div', { key: 'spinner', classes: [css.loading] }, [
				v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
			]);
		}

		const { questiontype } = question;
		const key = `${questiontype}-${instancekey}`;
		return w<QuestionType>(questiontype, {
			key,
			quizstore,
			inreview: this.inreview,
			hasReviewed: this.hasReviewed,
			isaudit,
			hasNextQuestion: this.has_next_question,
			onNextQuestion: this.next_question,
			hasPreviousQuestion: this.has_previous_question,
			onPreviousQuestion: this.previous_question,
			onFinish: this.has_next_section() ? this.finish_section : this.finish_quiz,
			updater: this.update_results,
			onReview: this.show_review,
			getQuestions: this.get_question_array,
			filterOn: this.filter_on,
			checkFor: this.check_for,
			hasNextSection: this.has_next_section
		} as MCSelectorProperties & WidgetProperties);
	}

	@beforeProperties()
	protected stateProperties(
		properties: QuestionListMPEProperties & WidgetProperties
	): QuestionListMPEProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._propsInited) {
			const paths = [['quiz'], ['info'], ['question'], ['isaudit'], ['instancekey']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			this._propsInited = true;
		}

		properties.quiz = get(path('quiz'));
		properties.info = get(path('info'));
		properties.isaudit = get(path('isaudit'));
		properties.question = get(path('question'));
		properties.instancekey = get(path('instancekey'));
		properties.quizsections = get(path('quizSections'));
		console.log(properties.quizsections || '-empty quizsections-');
		return properties;
	}
}

export default QuestionListMPE;
